import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Button,
} from '@mui/material';
import ViewIcon from '@mui/icons-material/Visibility'
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders } from '../auth';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchCustomers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setCustomers(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Handler for editing a customer.
  const handleEdit = (customer) => {
    // Navigate to the update customer page, passing the customer id
    navigate(`/customers/update/${customer._id}`, { state: { customer } });
  };

  // Handler for editing a customer.
  const handleView = (customer) => {
    // Navigate to the update customer page, passing the customer id
    navigate(`/customers/customer/${customer._id}`, { state: { customer } });
  };

  // Handler for deleting a customer.
  const handleDelete = async (customerId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers/${customerId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error('Failed to delete customer');
      }
      // Remove deleted customer from state.
      setCustomers((prev) => prev.filter((cust) => cust._id !== customerId));
    } catch (err) {
      console.error(err.message);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
          Customers
        </Typography>
        <Button variant="contained" onClick={() => navigate('/customers/create')}>
          Add Customer
        </Button>
      </Box>
      {customers.length === 0 ? (
        <Typography>No customers found.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Company Name</TableCell>
                <TableCell>Customer Code</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Logo</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((customer) => (
                <TableRow key={customer._id}>
                  <TableCell>{customer._id}</TableCell>
                  <TableCell>{customer.customerName}</TableCell>
                  <TableCell>{customer.customerCode || 'N/A'}</TableCell>
                  <TableCell>
                    {customer.customerAddress1}
                    {customer.customerAddress2 && `, ${customer.customerAddress2}`}, {customer.customerCity}, {customer.customerState} {customer.customerZip}
                  </TableCell>
                  <TableCell>
                    {customer.customerLogo ? (
                      <img
                        src={customer.customerLogo}
                        alt={customer.customerName}
                        style={{ maxWidth: '100px', maxHeight: '50px' }}
                      />
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell align="right">
                  <IconButton
                      color="primary"
                      onClick={() => handleView(customer)}
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(customer)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(customer._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Customers;
